// @shared
// Клиентская утилита для построения воксельных 3D-аксессуаров.
// Использует глобальный THREE, подключённый через CDN.

import { ACCESSORY_MODELS } from "../shared/accessoryModels"
import type { AccessoryModel, VoxelPart } from "../shared/accessoryModels"
import type { ShopSlot } from "../shared/shopTypes"

declare const THREE: any

export interface AttachedAccessory {
  slot: ShopSlot
  group: any
  groups?: any[]
  restore?: () => void
  update?: (t: number) => void
}

interface PaletteColors {
  skin?: string | number
  hair?: string | number
  clothes?: string | number
}

interface CharacterAnchors {
  hat: any
  face: any
  piercing: any
  neck: any
  back: any
  outfit: any
  outerwear: any
  bottom: any
  shoes: any
  pet: any
  head?: any
  leftArm?: any
  rightArm?: any
  leftLeg?: any
  rightLeg?: any
  hair?: any
  baseClothingMaterials?: any[]
  basePantsMaterials?: any[]
  baseShoeMaterials?: any[]
}

interface HeadwearFit {
  headSize: number
  hairTop: number
}

const REFERENCE_HEAD_SIZE = 0.792

const matCache = new Map<string, any>()

function getMaterial(color: number, opacity: number): any {
  const key = `${color}_${opacity}`
  let mat = matCache.get(key)
  if (!mat) {
    mat = new THREE.MeshToonMaterial({
      color,
      transparent: opacity < 1,
      opacity,
    })
    matCache.set(key, mat)
  }
  return mat
}

function toHex(value: string | number | undefined, fallback: number): number {
  if (value == null) return fallback
  if (typeof value === "number") return value
  const cleaned = value.replace("#", "")
  const parsed = parseInt(cleaned, 16)
  return Number.isFinite(parsed) ? parsed : fallback
}

function resolveColor(part: VoxelPart, palette: PaletteColors): number {
  if (part.usePalette === "skin") return toHex(palette.skin, part.color ?? 0xe0ac80)
  if (part.usePalette === "hair") return toHex(palette.hair, part.color ?? 0x2b1c10)
  if (part.usePalette === "clothes") return toHex(palette.clothes, part.color ?? 0x4a8fd1)
  return part.color ?? 0xcccccc
}

function applyAccessoryFit(group: any, anchor: any): void {
  const fit = anchor?.userData?.accessoryFit
  if (!fit) return
  group.scale.set(
    group.scale.x * (fit.x || 1),
    group.scale.y * (fit.y || 1),
    group.scale.z * (fit.z || 1),
  )
}

function applyHeadwearFit(group: any, anchor: any, model: AccessoryModel): void {
  const fit = anchor?.userData?.headwearFit as HeadwearFit | undefined
  if (!fit) return applyAccessoryFit(group, anchor)
  const scale = fit.headSize / REFERENCE_HEAD_SIZE
  group.scale.multiplyScalar(scale)
  group.updateMatrixWorld(true)
  const bounds = new THREE.Box3().setFromObject(group)
  const rule = model.headwear || { mode: "over" as const }
  const lift = (rule.lift || 0) * fit.headSize
  if (rule.mode === "enclose") {
    // Охватывающие модели проектируются вокруг центра головы. Их антенны и
    // декор не должны менять положение оболочки через общий bounding box.
    group.position.y += lift
    return
  }
  const overlap = (rule.overlap ?? (rule.mode === "cover" ? 0 : 0.035)) * fit.headSize
  // Закрытые уборы садятся на безопасную линию верхней части лба. Декоративные
  // элементы опираются на фактическую верхнюю точку выбранной причёски.
  const targetBottom = rule.mode === "cover"
    // Нижняя кромка закрывающего убора должна оставаться над глазами даже
    // в профиль. 0.21 — верхняя линия лба для текущей анатомии головы.
    ? fit.headSize * 0.21
    : fit.hairTop - overlap
  group.position.y += targetBottom - bounds.min.y + lift
}

export function buildAccessoryMesh(
  model: AccessoryModel,
  palette: PaletteColors = {},
  parts: VoxelPart[] = model.parts,
): any {
  const group = new THREE.Group()
  for (const p of parts) {
    const color = resolveColor(p, palette)
    const opacity = p.opacity ?? 1
    const mat = getMaterial(color, opacity)
    let geo: any
    if (p.shape === "sphere") geo = new THREE.SphereGeometry(0.5, 16, 10)
    else if (p.shape === "cylinder") geo = new THREE.CylinderGeometry(0.5, 0.5, 1, 20)
    else if (p.shape === "cone") geo = new THREE.ConeGeometry(0.5, 1, 16)
    else if (p.shape === "torus") {
      const tubeRatio = Math.max(0.05, Math.min(0.24, p.h / Math.max(p.w, p.d)))
      geo = new THREE.TorusGeometry(0.5 - tubeRatio / 2, tubeRatio / 2, 8, 28)
      geo.rotateX(Math.PI / 2)
    } else geo = new THREE.BoxGeometry(1, 1, 1)
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(p.x, p.y, p.z)
    if (p.shape === "torus") {
      const tubeRatio = Math.max(0.05, Math.min(0.24, p.h / Math.max(p.w, p.d)))
      mesh.scale.set(p.w, p.h / tubeRatio, p.d)
    } else {
      mesh.scale.set(p.w, p.h, p.d)
    }
    if (p.rotX) mesh.rotation.x = p.rotX
    if (p.rotY) mesh.rotation.y = p.rotY
    if (p.rotZ) mesh.rotation.z = p.rotZ
    mesh.castShadow = true
    group.add(mesh)
  }
  if (model.scale) group.scale.setScalar(model.scale)
  return group
}

/**
 * Прицепить аксессуары к персонажу через anchors-объекты.
 * Возвращает массив аксессуаров, у некоторых есть update(t) для процедурной анимации.
 */
export function attachAccessories(
  anchors: CharacterAnchors,
  items: Array<{ id: string; slot: string }>,
  palette: PaletteColors = {},
): AttachedAccessory[] {
  const attached: AttachedAccessory[] = []
  const clothingSlots = new Set<ShopSlot>(["outfit", "outerwear", "bottom", "shoes"])
  const hatCoveredByOutfit = items.some((item) => ACCESSORY_MODELS[item.id]?.coversHat)
  for (const item of items) {
    if (item.slot === "hat" && hatCoveredByOutfit) continue
    const model = ACCESSORY_MODELS[item.id]
    if (!model) continue
    const slot = item.slot as ShopSlot
    const anchor = (anchors as any)[slot]
    if (!anchor) continue

    const rootParts = model.parts.filter((part) => !part.attachTo || part.attachTo === slot)
    const mesh = buildAccessoryMesh(model, palette, rootParts)
    if (clothingSlots.has(slot)) applyAccessoryFit(mesh, anchor)
    if (slot === "hat") applyHeadwearFit(mesh, anchor, model)
    if (model.yOffset) mesh.position.y += model.yOffset
    if (model.zOffset) mesh.position.z += model.zOffset
    if (model.xOffset) mesh.position.x += model.xOffset

    const groups = [mesh]
    const geometryRestorers: Array<() => void> = []
    if (clothingSlots.has(slot)) {
      for (const target of ["head", "leftArm", "rightArm", "leftLeg", "rightLeg"] as const) {
        const targetParts = model.parts.filter((part) => part.attachTo === target)
        const targetAnchor = anchors[target]
        if (!targetParts.length || !targetAnchor) continue
        const targetMesh = buildAccessoryMesh({ parts: targetParts }, palette, targetParts)
        applyAccessoryFit(targetMesh, targetAnchor)
        targetAnchor.add(targetMesh)
        groups.push(targetMesh)
        if ((target === "leftArm" || target === "rightArm") && targetAnchor.userData?.setSleeveBottom) {
          // Наряд меняет длину рукава, поэтому открытая кисть должна начинаться
          // ровно у его нижней кромки. Так внутри рукава не остаётся второй
          // пересекающейся объём руки и не возникает z-fighting при анимации.
          const sleevePart = targetParts.reduce((largest, part) => {
            const volume = part.w * part.h * part.d
            const largestVolume = largest ? largest.w * largest.h * largest.d : -1
            return volume > largestVolume ? part : largest
          }, undefined as VoxelPart | undefined)
          if (sleevePart) {
            const previousBottom = targetAnchor.userData.sleeveBottom
            const fitY = targetAnchor.userData.accessoryFit?.y || 1
            const sleeveBottom = (sleevePart.y - sleevePart.h / 2) * fitY + targetMesh.position.y
            targetAnchor.userData.setSleeveBottom(sleeveBottom)
            geometryRestorers.push(() => targetAnchor.userData.setSleeveBottom(previousBottom))
          }
        }
      }
    }

    const visibilityChanges: Array<{ target: any; previous: boolean }> = []
    const hide = (targets: any[] | undefined) => {
      for (const target of targets || []) {
        visibilityChanges.push({ target, previous: target.visible })
        target.visible = false
      }
    }
    if (model.hideHair && anchors.hair) hide([anchors.hair])
    if (model.replacesBaseClothes) hide(anchors.baseClothingMaterials)
    if (model.replacesBasePants) hide(anchors.basePantsMaterials)
    if (model.replacesBaseShoes) hide(anchors.baseShoeMaterials)
    const restore = visibilityChanges.length || geometryRestorers.length
      ? () => {
          visibilityChanges.forEach(({ target, previous }) => { target.visible = previous })
          geometryRestorers.forEach((restoreGeometry) => restoreGeometry())
        }
      : undefined

    if (slot === "pet") {
      const baseY = mesh.position.y
      const baseRotY = mesh.rotation.y
      attached.push({
        slot, group: mesh, groups, restore,
        update: (t: number) => {
          mesh.position.y = baseY + Math.sin(t * 3) * 0.06 + 0.02
          mesh.rotation.y = baseRotY + Math.sin(t * 1.6) * 0.35
        },
      })
    } else if (slot === "back") {
      const baseY = mesh.position.y
      attached.push({
        slot, group: mesh, groups, restore,
        update: (t: number) => {
          mesh.position.y = baseY + Math.sin(t * 1.2) * 0.015
        },
      })
    } else {
      attached.push({ slot, group: mesh, groups, restore })
    }

    anchor.add(mesh)
  }
  return attached
}

export function detachAccessories(attached: AttachedAccessory[]): void {
  for (const a of attached) {
    for (const group of a.groups || [a.group]) {
      if (group.parent) group.parent.remove(group)
    }
    a.restore?.()
  }
}
